import React, { useEffect, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { InstalledBaseNode } from '../../codegen/graphql';
import { useNodeActions } from './nodeActions';
import { MoveInstalledBaseNodeSuccessInfo } from '../MoveInstalledBaseNodeModal';

export interface NodeContextMenuState {
    node: InstalledBaseNode;
    position: { top: number; left: number };
}

interface Props {
    state: NodeContextMenuState | null;
    isRoot: boolean;
    /** True when the column's rows lie inside the trash (moving out = restore). */
    inTrash?: boolean;
    /** True when the column's rows lie inside a User Pool (rows carry no pathNodes). */
    inUserPool?: boolean;
    onClose: () => void;
    refetchFunction?: () => void;
    deleteNodeCallback?: (deletedNodeId: string) => void;
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
    /** Opens the node detail panel on Information (the Edit/Details action). */
    onEditNodeDetail?: (nodeId: string) => void;
}

const NodeRowContextMenu = ({
    state,
    isRoot,
    inTrash,
    inUserPool,
    onClose,
    refetchFunction,
    deleteNodeCallback,
    moveNodeCallback,
    onEditNodeDetail,
}: Props) => {
    // Retain the last target node so that modals opened from the menu keep
    // their node context after the menu itself closes (state goes back to null
    // but the modal stays mounted until dismissed).
    const [activeNode, setActiveNode] = useState<InstalledBaseNode | null>(null);

    useEffect(() => {
        if (state?.node) setActiveNode(state.node);
    }, [state?.node]);

    const { items, modals } = useNodeActions({
        targetNode: activeNode ?? ({} as InstalledBaseNode),
        isRoot,
        context: 'listRow',
        inTrash,
        inUserPool,
        refetchFunction,
        deleteNodeCallback,
        moveNodeCallback,
        onEditNode: onEditNodeDetail && activeNode?.id
            ? () => onEditNodeDetail(activeNode.id)
            : undefined,
    });

    // A node without any active action (the TRASH node itself) gets no context
    // menu at all instead of an empty popup.
    const hasActiveItems = items.some((it) => it.active);

    return (
        <>
            <Menu
                open={state !== null && hasActiveItems}
                onClose={onClose}
                anchorReference="anchorPosition"
                anchorPosition={state?.position ?? undefined}
            >
                {items.map((it, idx) =>
                    it.active ? (
                        <MenuItem
                            key={idx}
                            disabled={it.disabled}
                            onClick={() => {
                                it.onClick?.();
                                onClose();
                            }}
                        >
                            {it.item}
                        </MenuItem>
                    ) : null
                )}
            </Menu>
            {modals}
        </>
    );
};

export default NodeRowContextMenu;
